// Catalog.ID Client — Trust Detail Pane
// ============================================================================
// Shows trust expression detail and allows updating/removing trust.

import {
    createElement, createFieldGroup, createFormRow, createEmptyState, renderPaneActions,
} from '@catalog-shell';
import type {
    PaneActionDescriptor, PaneInstance, PaneType, PaneTypeRenderContext,
} from '@catalog-shell';
import * as trustApi from '../api/trust';
import { generateTrustCommitment, signTrustExpression } from '../crypto/trust';
import { identity } from '../state/identity';
import type { TrustExpressionSummary } from '../api/types';
import { PANE_IDS } from '../shell-config';

const SCORE_LABELS: Record<number, string> = {
    1: 'Distrust',
    2: 'Low',
    3: 'Neutral',
    4: 'Trust',
    5: 'High Trust',
};

// ----------------------------------------------------------------------------
// Title-row action
//
// Withdrawing the expression is the pane's only verb (the 1-5 buttons are a
// value selector, not a verb), so it rides inline in the title row rather than
// in a field group of its own — see shell USAGE, "Where a button belongs".
// ----------------------------------------------------------------------------

/** What the title row needs to know about the expression on screen. */
export interface TrustDetailActionState {
    hasExpression: boolean;
    /** The removal is in flight. */
    busy: boolean;
}

interface TrustDetailState extends TrustDetailActionState {
    ctx: PaneTypeRenderContext;
    expr: TrustExpressionSummary;
    /** Points at the error line of the body as last rendered. */
    showError: (message: string) => void;
}

const states = new WeakMap<PaneInstance, TrustDetailState>();

/** Pure: the single verb, and whether it is currently available. */
export function planTrustDetailActions(
    st: TrustDetailActionState | undefined,
    exec: () => void,
): PaneActionDescriptor[] {
    if (!st?.hasExpression) return [];
    return [{
        key: 'remove',
        label: 'Remove Trust Expression',
        variant: 'danger',
        disabled: st.busy,
        title: st.busy ? 'Removing this trust expression…' : undefined,
        execute: () => exec(),
    }];
}

function trustDetailActions(instance: PaneInstance): HTMLElement[] {
    const st = states.get(instance);
    return renderPaneActions(planTrustDetailActions(st, () => { void removeTrust(st!); }));
}

async function removeTrust(st: TrustDetailState): Promise<void> {
    if (st.busy) return;
    const { ctx, expr } = st;
    const ok = await ctx.shell.confirm({
        title: 'Remove Trust Expression',
        message: `Remove your trust expression for ${expr.targetCid}? `
            + 'It stops counting towards their trust score.',
        confirmLabel: 'Remove',
        confirmVariant: 'no',
    });
    if (!ok) return;

    st.busy = true;
    ctx.shell.refreshPaneActions(ctx.instance.instanceId);
    try {
        await trustApi.removeTrust(expr.targetCid);
        ctx.surface?.closePane(ctx.instance.instanceId);
    } catch (e) {
        st.showError(e instanceof Error ? e.message : 'Remove failed');
        st.busy = false;
        ctx.shell.refreshPaneActions(ctx.instance.instanceId);
    }
}

export function createTrustDetailPane(): PaneType {
    return {
        typeId: PANE_IDS.trustDetail,
        kind: 'leaf',
        defaultTitle: 'Trust Detail',
        capabilities: [],
        tabBarActions: trustDetailActions,
        onDestroy: (instance) => { states.delete(instance); },
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            const expr = ctx.instance.context['trustExpression'] as TrustExpressionSummary | undefined;
            if (!expr) {
                container.appendChild(createEmptyState({ message: 'No trust expression selected' }));
                return container;
            }
            const st: TrustDetailState = {
                ctx,
                expr,
                hasExpression: true,
                busy: false,
                showError: () => {},
            };
            states.set(ctx.instance, st);
            void renderDetail(container, st);
            return container;
        },
    };
}

async function renderDetail(container: HTMLElement, st: TrustDetailState): Promise<void> {
    const { ctx, expr } = st;
    // Info section
    const infoGroup = createFieldGroup({ title: 'Trust Expression' });

    const cidRow = createFormRow({ label: 'Target' });
    const cidLink = createElement('a', {
        className: 'cid-profile-link',
        textContent: expr.targetCid,
    });
    cidLink.setAttribute('href', '#');
    cidLink.addEventListener('click', (e) => {
        e.preventDefault();
        ctx.surface?.openPane({
            typeId: PANE_IDS.publicProfile,
            title: expr.targetCid,
            context: { cid: expr.targetCid },
        }, { originPaneId: ctx.instance.instanceId });
    });
    cidRow.content.appendChild(cidLink);
    infoGroup.body.appendChild(cidRow.element);

    const scoreRow = createFormRow({ label: 'Rating' });
    scoreRow.content.appendChild(createElement('span', {
        textContent: `${expr.score}/5 — ${SCORE_LABELS[expr.score] ?? ''}`,
    }));
    infoGroup.body.appendChild(scoreRow.element);

    const visRow = createFormRow({ label: 'Subgraph Visible' });
    visRow.content.appendChild(createElement('span', {
        textContent: expr.subgraphVisible ? 'Yes' : 'No',
    }));
    infoGroup.body.appendChild(visRow.element);

    const dateRow = createFormRow({ label: 'Created' });
    dateRow.content.appendChild(createElement('span', {
        textContent: new Date(expr.creationTime).toLocaleString(),
    }));
    infoGroup.body.appendChild(dateRow.element);

    if (expr.updatedAt) {
        const updRow = createFormRow({ label: 'Updated' });
        updRow.content.appendChild(createElement('span', {
            textContent: new Date(expr.updatedAt).toLocaleString(),
        }));
        infoGroup.body.appendChild(updRow.element);
    }

    container.appendChild(infoGroup.element);

    // Update score section
    const updateGroup = createFieldGroup({ title: 'Update Rating' });
    const btnRow = createElement('div', { className: 'cid-trust-score-buttons' });

    for (let s = 1; s <= 5; s++) {
        const btn = createElement('button', {
            className: `cs-btn cs-btn--small ${s === expr.score ? 'cs-btn--yes' : 'cs-btn--default'}`,
            textContent: `${s} — ${SCORE_LABELS[s]}`,
        });
        btn.addEventListener('click', async () => {
            try {
                // Generate fresh cryptographic commitment for the update
                const sourceCid = identity.getActiveCid()!;
                const { nonce, commitment } = await generateTrustCommitment(sourceCid);
                const signature = signTrustExpression(expr.targetCid, s);
                await trustApi.expressTrust(expr.targetCid, s, signature, commitment, nonce);
                expr.score = s;
                // Refresh pane
                container.innerHTML = '';
                void renderDetail(container, st);
            } catch (e) {
                errorEl.textContent = e instanceof Error ? e.message : 'Update failed';
                errorEl.classList.add('cid-pane-error--visible');
            }
        });
        btnRow.appendChild(btn);
    }
    updateGroup.body.appendChild(btnRow);

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    updateGroup.body.appendChild(errorEl);

    container.appendChild(updateGroup.element);

    st.showError = (message) => {
        errorEl.textContent = message;
        errorEl.classList.add('cid-pane-error--visible');
    };
    // The body is rebuilt on every rating click, so re-plan the row each time.
    ctx.shell.refreshPaneActions(ctx.instance.instanceId);
}
